import * as React from 'react'
import { styled } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'

import { DisplayMode } from '../../../../context'
import { SIDEBAR_MAX_WIDTH } from '../../style'
import { EDIT_MODE_SMALL_LAPTOP } from '../../constants'

export function NewsPermalinkSkeleton() {
  const isEditMode = React.useContext(DisplayMode) === 'edit'

  return (
    <Page $isEditMode={isEditMode}>
      <Content $isEditMode={isEditMode}>
        <Return $isEditMode={isEditMode}>
          <plume.glyphs.stroke.ArrowLeft size={16} />
          <plume.styles.skeleton.Text size="small" width={100} />
        </Return>

        <Card isEditMode={isEditMode} />
      </Content>

      <Aside $isEditMode={isEditMode}>
        <plume.styles.skeleton.Placeholder height={150}>
          <plume.styles.skeleton.Text size="medium" width={60} />
          <plume.styles.skeleton.Text size="medium" width={40} />
          <plume.styles.skeleton.Rectangle height={40} width={80} />
        </plume.styles.skeleton.Placeholder>

        <RelativeBox>
          <plume.styles.skeleton.Circle radius={56} />

          <plume.styles.skeleton.Rectangle height={250} />
        </RelativeBox>
      </Aside>
    </Page>
  )
}

function Card({ isEditMode }: { isEditMode: boolean }) {
  return (
    <CardWrapper $isEditMode={isEditMode}>
      <ImageSkeleton $isEditMode={isEditMode} />

      <plume.styles.skeleton.Placeholder height={250}>
        <plume.styles.skeleton.Text size="large" width={60} />
        <plume.styles.skeleton.Text size="small" width={50} />

        <CardContent>
          <plume.styles.skeleton.Text size="small" width={98} />
          <plume.styles.skeleton.Text size="small" width={98} />

          <plume.styles.skeleton.Text size="small" width={98} />
          <plume.styles.skeleton.Text size="small" width={98} />

          <plume.styles.skeleton.Text size="small" width={98} />
          <plume.styles.skeleton.Text size="small" width={65} />
        </CardContent>

        <BottomInformation>
          <Comment>
            <plume.glyphs.stroke.Bubble size={11} />
            <plume.styles.skeleton.Text size="small" width={100} />
          </Comment>

          <See>
            <plume.styles.skeleton.RoundedRectangle height={32} width={117} />
          </See>
        </BottomInformation>
      </plume.styles.skeleton.Placeholder>
    </CardWrapper>
  )
}

const BottomInformation = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
  margin-top: 48px;
`

const Comment = styled.div`
  display: flex;
  gap: 4px;
  width: 127px;
  align-items: center;

  ${plume.glyphs.stroke.Bubble} {
    color: ${plume.COLORS.PRIMARY_SAND_200};
  }
`

const See = styled.div`
  display: flex;
  gap: 4px;
  width: 155px;
  align-items: center;

  ${plume.glyphs.stroke.ArrowRight} {
    color: ${plume.COLORS.PRIMARY_SAND_200};
  }
`

const Page = styled.div<{ $isEditMode: boolean }>`
  padding: 0 10px;
  margin-top: 30px;

  @media screen and ${({ $isEditMode }) => ($isEditMode ? EDIT_MODE_SMALL_LAPTOP : plume.BREAKPOINTS.LAPTOP)} {
    box-sizing: border-box;
    display: flex;
    margin: 60px auto;
    max-width: 1200px;
  }
`

const RelativeBox = styled.div`
  position: relative;
  margin-bottom: 30px;

  ${plume.styles.skeleton.Circle} {
    position: absolute;
    top: -28px;
    left: 50%;
    transform: translateX(-50%);
  }
`

const Aside = styled.div<{ $isEditMode: boolean }>`
  margin-top: 30px;

  @media screen and ${({ $isEditMode }) => ($isEditMode ? EDIT_MODE_SMALL_LAPTOP : plume.BREAKPOINTS.LAPTOP)} {
    margin-left: 45px;
    margin-top: 0;
    max-width: ${({ $isEditMode }) => ($isEditMode ? '336px' : SIDEBAR_MAX_WIDTH)};
    min-width: 336px;
    width: ${({ $isEditMode }) => ($isEditMode ? 'fit-content' : SIDEBAR_MAX_WIDTH)};
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP_L} {
    flex: none;
    max-width: ${SIDEBAR_MAX_WIDTH};
  }

  ${plume.styles.skeleton.Placeholder} {
    align-items: center;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    margin-bottom: 48px;
    padding: 24px 21px;

    ${plume.styles.skeleton.Rectangle} {
      margin-top: 14px;
    }
  }
`

const CardWrapper = styled.div<{ $isEditMode: boolean }>`
  border-radius: 4px;
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  padding: 16px;
  position: relative;

  ${plume.styles.skeleton.Placeholder} {
    border: 0px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding: 32px;
  }

  ${plume.styles.skeleton.Placeholder} {
    justify-content: space-between;
    flex-direction: column;
    display: flex;
    background: ${plume.COLORS.PRIMARY_GREY_000};
  }

  @media screen and ${({ $isEditMode }) => ($isEditMode ? EDIT_MODE_SMALL_LAPTOP : plume.BREAKPOINTS.LAPTOP)} {
    height: 632px;
  }
`

const Content = styled.div<{ $isEditMode: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin: 0 auto;

  ${plume.styles.skeleton.Placeholder} {
    box-sizing: border-box;
    padding: 0px;
    text-align: center;
  }

  @media screen and ${({ $isEditMode }) => ($isEditMode ? EDIT_MODE_SMALL_LAPTOP : plume.BREAKPOINTS.LAPTOP)} {
    max-width: 760px;
  }
`

const Return = styled.div<{ $isEditMode: boolean }>`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 30px;
  width: 150px;

  ${plume.glyphs.stroke.ArrowLeft} {
    color: ${plume.COLORS.PRIMARY_SAND_200};
    position: relative;
    top: 2px;
  }

  @media screen and ${({ $isEditMode }) => ($isEditMode ? EDIT_MODE_SMALL_LAPTOP : plume.BREAKPOINTS.LAPTOP)} {
    width: 350px;
  }
`

const CardContent = styled.div`
  margin-top: 24px;
`

const ImageSkeleton = styled(plume.styles.skeleton.Rectangle)<{ $isEditMode: boolean }>`
  height: 199px !important;

  @media screen and ${({ $isEditMode }) => ($isEditMode ? EDIT_MODE_SMALL_LAPTOP : plume.BREAKPOINTS.LAPTOP)} {
    height: 416px !important;
  }
`
